/**
 * LearningModeBadge Component
 *
 * Displays when the variance learning system is still collecting data
 * (less than minimum samples required for confident predictions).
 *
 * Part of US-P3-001: Statistical Process Variance Learning
 */

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { FlaskConical, CheckCircle } from 'lucide-react'

// Default minimum samples required for learning to complete
const DEFAULT_MIN_SAMPLES = 10

/**
 * Learning mode badge for variance predictions
 *
 * @param {Object} props
 * @param {number} props.sampleCount - Current number of samples collected
 * @param {number} [props.minSamples=10] - Minimum samples required for confident predictions
 * @param {boolean} [props.showProgress=true] - Whether to show sample count progress
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Badge indicating learning status
 *
 * @example
 * <LearningModeBadge sampleCount={8} />
 * // Renders: Learning (8/10 samples)
 *
 * @example
 * <LearningModeBadge sampleCount={15} minSamples={10} />
 * // Renders: Ready (checkmark)
 */
export default function LearningModeBadge({
  sampleCount = 0,
  minSamples = DEFAULT_MIN_SAMPLES,
  showProgress = true,
  className
}) {
  const isLearning = sampleCount < minSamples
  const progress = Math.min(sampleCount, minSamples)

  if (isLearning) {
    return (
      <Badge
        variant="outline"
        className={cn(
          'font-medium gap-1.5',
          'bg-amber-50 text-amber-700 border-amber-200',
          className
        )}
        role="status"
        aria-label={`Learning mode: ${sampleCount} of ${minSamples} samples collected`}
      >
        <FlaskConical className="h-3 w-3" aria-hidden="true" />
        <span>Learning</span>
        {showProgress && (
          <span className="font-mono text-xs opacity-75">
            ({progress}/{minSamples})
          </span>
        )}
      </Badge>
    )
  }

  // Sufficient samples - show ready state
  return (
    <Badge
      variant="outline"
      className={cn(
        'font-medium gap-1.5',
        'bg-emerald-50 text-emerald-700 border-emerald-200',
        className
      )}
      role="status"
      aria-label="Predictions ready with sufficient sample data"
    >
      <CheckCircle className="h-3 w-3" aria-hidden="true" />
      <span>Ready</span>
      {showProgress && (
        <span className="font-mono text-xs opacity-75">
          ({sampleCount})
        </span>
      )}
    </Badge>
  )
}

/**
 * Compact learning indicator (just the icon with tooltip)
 *
 * @param {Object} props
 * @param {number} props.sampleCount - Current number of samples
 * @param {number} [props.minSamples=10] - Minimum samples required
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Compact icon indicator
 */
export function LearningIndicator({
  sampleCount = 0,
  minSamples = DEFAULT_MIN_SAMPLES,
  className
}) {
  const isLearning = sampleCount < minSamples

  if (isLearning) {
    return (
      <FlaskConical
        className={cn('h-4 w-4 text-amber-500', className)}
        aria-label={`Learning: ${sampleCount}/${minSamples} samples`}
        title={`Learning mode: ${sampleCount} of ${minSamples} samples collected`}
      />
    )
  }

  return (
    <CheckCircle
      className={cn('h-4 w-4 text-emerald-500', className)}
      aria-label={`Ready: ${sampleCount} samples`}
      title={`Predictions ready with ${sampleCount} samples`}
    />
  )
}
